"use client"

import React, { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { cn } from "@/lib/utils";
import Footer from "../footer";
import UploadHandler from "./upload-handler";
import UnitLoadImageUploader from "./unit-load-image-uploader";
import { useGetProjectMediaQuery, useUploadMediaMutation } from "@/redux/slices/media/mediaApiSlice";
import { showSuccessToast, showErrorToast } from "../toast/toast";
import { backendApiUrl } from "@/lib/config";
import oktaService from "@/services/OktaService";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { useSearchParams } from "react-router-dom";
// Enhanced ImageCategory type to include unit-load-images
type ImageCategory = "gpsRoute" | "preship" | "postship" | "stacking" | "unit-load-images";

// Enhanced MediaItem interface with subCategory for unit-load-images
interface MediaItem {
  id: number;
  url: string;
  contentType: 'image' | 'video' | 'pdf';
  type: ImageCategory;
  palletId: number;
  file?: File;
  // Fields for unit-load-images
  subCategory?: 'specNumber' | 'palletDrawing' | 'topView';
  originalFilename?: string;
  fileSize?: number;
  metadata?: Record<string, unknown>;
  // UI state fields
  localUrl?: string;
  isLoading?: boolean;
  hasError?: boolean;
}

interface UploadValues {
  mediaItems: {
    palletId: number;
    url: string;
    contentType: 'image' | 'video' | 'pdf';
    type: ImageCategory;
    subCategory?: string;
    originalFilename?: string;
    fileSize?: number;
  }[];
  projectId: string | number;
}

interface ImagesProps {
  projectId?: string;
  isOffline?: boolean;
  onOfflineSubmit?: (values: UploadValues) => Promise<void>;
}

export default function Images({
  // These props are kept for future use even if not currently used
  isOffline,
  onOfflineSubmit
}: ImagesProps) {
  const { projectId } = useSelector((state: RootState) => state.define)
  const { palletTypes } = useSelector((state: RootState) => state.pallets);
  const { data: projectMedia, isLoading, refetch } = useGetProjectMediaQuery(projectId,);
  const [createMedia] = useUploadMediaMutation();
  const [groupedMedia, setGroupedMedia] = useState<Record<number, Record<ImageCategory, MediaItem[]>>>({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Use searchParams for state management
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPalletId, setCurrentPalletId] = useState<string>(
    searchParams.get('palletId') || ''
  );
  const [currentImageTab, setCurrentImageTab] = useState<string>(
    searchParams.get('imageTab') || 'preship'
  );
  const [isNav, setIsNav] = useState(searchParams.get('isNav') !== 'false');
  const [uploading, setUploading] = useState(false);

  // Set initial pallet ID when data is loaded
  useEffect(() => {
    if (palletTypes.length > 0 && !currentPalletId) {
      const initialPalletId = palletTypes[0]?.id?.toString() || '';
      setCurrentPalletId(initialPalletId);

      // Update URL params with initial values
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('palletId', initialPalletId);
        return newParams;
      });
    }
  }, [palletTypes, currentPalletId, setSearchParams]);

  // Sync URL params with component state when tabs change
  useEffect(() => {
    if (currentPalletId || currentImageTab) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        if (currentPalletId) {
          newParams.set('palletId', currentPalletId);
        }
        if (currentImageTab) {
          newParams.set('imageTab', currentImageTab);
        }
        newParams.set('isNav', isNav.toString());
        return newParams;
      });
    }
  }, [currentPalletId, currentImageTab, isNav, setSearchParams]);

  // Handle media data grouping
  useEffect(() => {
    if (projectMedia) {
      const newGroupedMedia = projectMedia.reduce((acc: Record<number, Record<ImageCategory, MediaItem[]>>, item: MediaItem) => {
        if (!acc[item.palletId]) {
          acc[item.palletId] = {} as Record<ImageCategory, MediaItem[]>;
        }
        if (!acc[item.palletId][item.type]) {
          acc[item.palletId][item.type] = [];
        }
        acc[item.palletId][item.type].push(item);
        return acc;
      }, {} as Record<number, Record<ImageCategory, MediaItem[]>>);

      setGroupedMedia(newGroupedMedia);
    }
  }, [projectMedia]);

  // Custom tab change handlers to update both state and URL
  const handlePalletTabChange = (value: string) => {
    setCurrentPalletId(value);
  };

  const handleImageTabChange = (value: string) => {
    setCurrentImageTab(value);
  };

  const toggleNav = () => {
    const newIsNav = !isNav;
    setIsNav(newIsNav);
  };

  const onSubmit = async () => {
    try {
      setUploading(true);
      // Find new media items to upload (those with file property)
      const newMediaItems = Object.values(groupedMedia).flatMap(category =>
        Object.values(category).flat().filter(item =>
          // Include any item with a file property regardless of type
          item.file
        )
      );

      if (newMediaItems.length === 0) {
        showSuccessToast("No new images to upload");
        setUploading(false);
        return;
      }

      // Upload files in batches to avoid overwhelming the server
      const batchSize = 5;
      const uploadedMedia = [];
      const failedUploads = [];

      for (let i = 0; i < newMediaItems.length; i += batchSize) {
        const batch = newMediaItems.slice(i, i + batchSize);

        // Upload batch in parallel
        const batchResults = await Promise.allSettled(
          batch.map(async (item) => {
            try {
              // Skip unit-load-images as they are handled immediately during upload
              if (item.type === 'unit-load-images') {
                return {
                  palletId: item.palletId,
                  url: item.url,
                  contentType: item.contentType,
                  type: item.type,
                  originalItem: item,
                  alreadyInDb: true // Mark that it's already in the database
                };
              }

              // Check file size - regular uploads now have 4MB limit
              const fileSize = (item.file as File).size;
              if (fileSize > 4 * 1024 * 1024) {
                throw new Error(`File size exceeds 4MB limit: ${(item.file as File).name}`);
              }

              // Create form data for each file
              const formData = new FormData();
              formData.append('media', item.file as File);
              formData.append('palletId', item.palletId.toString());
              formData.append('imageType', item.type);

              const oktaUser = await oktaService.getUser();
              // Upload to our backend API which handles Azure Blob Storage
              const response = await fetch(`${backendApiUrl}/media/upload-file`, {
                method: 'POST',
                body: formData,
                // Add additional headers to possibly help with CORS
                headers: {
                  Authorization: `Bearer ${oktaUser.access_token}`,
                  'Accept': 'application/json',
                  // Don't set Content-Type header with FormData - browser will set it with boundary
                },
                credentials: 'include', // Include cookies if your API requires authentication
              });

              if (!response.ok) {
                // Special handling for 405 Method Not Allowed
                if (response.status === 405) {
                  console.error('405 Method Not Allowed error. API endpoint might not support POST method.');
                  throw new Error('API endpoint doesn\'t support POST method. Please contact IT support.');
                }

                let errorMessage = 'Upload failed';
                try {
                  const errorData = await response.json();
                  errorMessage = errorData.error || errorMessage;
                  console.error('Server error response:', errorData);
                } catch (_jsonError) {
                  // If the response isn't JSON, try to get text
                  try {
                    const textError = await response.text();
                    console.error('Server error text:', textError);
                  } catch (_textError) {
                    // If we can't get text either, just log the status
                    console.error('Unable to parse error response. Status:', response.status);
                  }
                }

                throw new Error(errorMessage);
              }

              // Get the response data with SAS URL
              const responseData = await response.json();
              const { url, blobName, contentType, mediaId } = responseData;

              // Check if a database record was created
              const alreadyInDb = !!mediaId;

              console.log(`Successfully uploaded file with URL: ${url.substring(0, 50)}...`);

              return {
                palletId: item.palletId,
                url,
                blobName, // Store the blob name for future reference
                contentType: contentType || item.contentType,
                type: item.type,
                originalFilename: (item.file as File).name,
                fileSize: (item.file as File).size,
                originalItem: item,
                alreadyInDb,
                id: mediaId // Include the ID if one was created
              };
            } catch (error) {
              console.error('Error uploading file:', error);
              throw error;
            }
          })
        );

        // Process batch results
        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            uploadedMedia.push(result.value);
          } else {
            failedUploads.push({
              item: batch[index],
              error: result.reason.message || 'Unknown error'
            });
          }
        });
      }

      // If we have some successful uploads, save them to the database
      if (uploadedMedia.length > 0) {
        // Filter out items that are already in the database
        const mediaToSave = uploadedMedia
          .filter(item => !item.alreadyInDb && !item.id)
          .map(({ originalItem: _originalItem, alreadyInDb: _alreadyInDb, ...item }) => item);

        if (mediaToSave.length > 0) {
          // Save the media items in the database
          await createMedia({ mediaItems: mediaToSave, projectId }).unwrap();
        }

        // Update local state
        setGroupedMedia(prevState => {
          const newState = { ...prevState };
          uploadedMedia.forEach(item => {
            if (newState[item.palletId]?.[item.type]) {
              const category = newState[item.palletId][item.type];
              const index = category.findIndex(mediaItem =>
                mediaItem === item.originalItem || mediaItem.url === item.url
              );
              if (index !== -1) {
                category[index] = {
                  ...category[index],
                  ...item,
                  file: undefined,
                  id: item.id || category[index].id || Date.now() // Ensure we have an ID
                };
              }
            }
          });
          return newState;
        });
      }

      // Show appropriate toast messages
      if (failedUploads.length === 0) {
        showSuccessToast("All images uploaded successfully!");
      } else if (uploadedMedia.length > 0) {
        showSuccessToast(`Uploaded ${uploadedMedia.length} images. ${failedUploads.length} failed.`);
      } else {
        showErrorToast("All uploads failed!");
      }

      // Only mark as no unsaved changes if everything uploaded successfully
      if (failedUploads.length === 0) {
        setUnsavedChanges(false);
      }
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Upload failed!");
      console.error('Failed to save media:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMedia = (mediaId: number) => {
    setGroupedMedia(prevState => {
      const newState = { ...prevState };
      // Find and remove the media item from the appropriate category
      Object.keys(newState).forEach(palletId => {
        Object.keys(newState[Number(palletId)]).forEach(category => {
          newState[Number(palletId)][category as ImageCategory] =
            newState[Number(palletId)][category as ImageCategory].filter(item => item.id !== mediaId);
        });
      });
      return newState;
    });
    setUnsavedChanges(true);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full flex flex-col h-[calc(100vh-140px)]">
      <Tabs
        value={currentPalletId}
        onValueChange={handlePalletTabChange}
        className="w-full flex flex-1 overflow-hidden"
      >
        {
          isNav ?
            <TabsList className="w-[170px] overflow-y-auto h-full bg-light-blue flex flex-col justify-start space-y-4 pt-10 relative">
              <ChevronsLeft onClick={toggleNav} className="cursor-pointer absolute top-2 right-2 text-blue" />
              <p className="text-md text-black text-left font-semibold px-5">Types</p>
              <div className=" w-full">
                {palletTypes.map((pallet) => (
                  <TabsTrigger
                    key={pallet.id}
                    value={pallet.id?.toString() || ''}
                    className="flex justify-center items-center py-4  text-left w-full"
                  >
                    <div className="flex flex-col gap-1">
                      <div>{pallet.variant}</div>
                      <div>{pallet.name}</div>
                    </div>
                  </TabsTrigger>
                ))}
              </div>
            </TabsList>
            :
            <TabsList className="w-[40px] relative z-50 h-full bg-light-blue flex flex-col items-start justify-start pt-10 overflow-y-auto">
              <ChevronsRight onClick={toggleNav} className="cursor-pointer absolute top-2 right-2 text-blue" />
            </TabsList>
        }
        <div className="flex-1 overflow-hidden">
          {palletTypes.map((pallet) => (
            <TabsContent
              key={pallet.id}
              value={pallet.id?.toString() || ''}
              className={cn(
                "h-full overflow-y-auto p-8 pb-[100px]",
                pallet.variant !== "Ship Test Truck Layout" && "space-y-4"
              )}
            >
              <div className="w-full">
                <h3 className="text-lg text-black font-semibold">
                  Upload Photos
                </h3>
                <Tabs
                  value={currentImageTab}
                  onValueChange={handleImageTabChange}
                  className="w-full mt-5"
                >
                  <TabsList className="flex justify-start gap-3 bg-white">
                    {["gpsRoute", "preship", "postship", "stacking", 'unit-load-images', 'Pre-Ship Rql', 'Post-Ship Rql', 'Post-Stack Rql'].map(
                      (item) => (
                        <TabsTrigger
                          key={item}
                          value={item}
                          className="w-fit data-[state=active]:bg-blue data-[state=active]:text-white border border-blue rounded-lg py-2 text-sm data-[state=inactive]:bg-states-disabled px-5 data-[state=inactive]:border-0"
                        >
                          {item.charAt(0).toUpperCase() + item.slice(1)}
                        </TabsTrigger>
                      )
                    )}
                  </TabsList>
                  {pallet.id && ["gpsRoute", "preship", "postship", "stacking", 'unit-load-images', 'Pre-Ship Rql', 'Post-Ship Rql', 'Post-Stack Rql'].map(
                    (tab) => (
                      <TabsContent
                        value={tab}
                        key={`${pallet.id}-${tab}`}
                      >
                        {tab === 'unit-load-images' ? (
                          <UnitLoadImageUploader
                            refetch={refetch}
                            key={`${pallet.id}-${tab}-unitload`}
                            palletId={Number(pallet.id)!}
                            category={tab}
                            mediaItems={groupedMedia[Number(pallet.id)]?.[tab as ImageCategory] || []}
                            onAddMedia={(newItem) => {
                              const updatedMedia = {
                                ...groupedMedia,
                                [Number(pallet.id)]: {
                                  ...groupedMedia[Number(pallet.id)],
                                  [tab]: [...(groupedMedia[Number(pallet.id)]?.[tab as ImageCategory] || []), newItem]
                                }
                              };
                              setGroupedMedia(updatedMedia);
                              setUnsavedChanges(true);
                            }}
                            onDeleteMedia={handleDeleteMedia}
                          />
                        ) : (
                          <UploadHandler
                            refetch={refetch}
                            key={`${pallet.id}-${tab}-uploadhandler`}
                            palletId={Number(pallet.id)!}
                            category={tab as ImageCategory}
                            mediaItems={groupedMedia[Number(pallet.id)]?.[tab as ImageCategory] || []}
                            onAddMedia={(newItem) => {
                              const updatedMedia = {
                                ...groupedMedia,
                                [Number(pallet.id)]: {
                                  ...groupedMedia[Number(pallet.id)],
                                  [tab]: [...(groupedMedia[Number(pallet.id)]?.[tab as ImageCategory] || []), newItem]
                                }
                              };
                              setGroupedMedia(updatedMedia);
                              setUnsavedChanges(true);
                            }}
                            onDeleteMedia={handleDeleteMedia}
                            projectId={Number(projectId)}
                          />
                        )}
                      </TabsContent>
                    )
                  )}
                </Tabs>
              </div>
            </TabsContent>
          ))}
        </div>
      </Tabs>
      <Footer isLoading={uploading} noNext={true} hasUnsavedChanges={false} layout onSubmit={onSubmit} />
    </div>
  );
}
